"use client";
/**
 * Site-wide motion — ported from the v6 design repo's site-motion.tsx (scroll
 * reveals via WAAPI, photo parallax, pointer tilt, reading-progress bar), driven
 * through DOM [data-*] attributes.
 *
 * Difference from v6 (on purpose): motion is gated behind a user toggle and
 * defaults OFF when the OS requests reduced motion — v6 ran it unconditionally.
 * The engine only wires up when motion is on; a fixed "MOTION ON / PAUSE MOTION"
 * control persists the choice, and html[data-motion="off"] pauses CSS keyframe
 * animation too. Styles in src/styles/motion.css.
 */
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import "../../styles/motion.css";

function prefersReduced() {
    try {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
        return false;
    }
}

export default function SiteMotion() {
    const path = usePathname();
    /**
     * ‼ STARTS OFF, AND MUST. This was `useState(true)`, and the two effects
     * below both run in that first commit with the stale `true`: the persist
     * effect wrote `data-motion="on"`, and the engine effect wired up the
     * observers and actually STARTED reveal animations — all before the init
     * effect could read `prefers-reduced-motion` and switch it off. A visitor
     * who asked the OS for reduced motion got motion anyway.
     *
     * Starting off costs a motion-enabled visitor nothing: one commit with no
     * animation running, which is invisible, whereas the inverse was not.
     */
    const [on, setOn] = useState(false);

    // Initial state: stored choice wins; otherwise off if the OS asks for reduced motion.
    useEffect(() => {
        let initial = !prefersReduced();
        try {
            const saved = localStorage.getItem("abm-motion");
            if (saved === "on") initial = true;
            else if (saved === "off") initial = false;
        } catch {
            /* storage optional */
        }
        setOn(initial);
    }, []);

    useEffect(() => {
        document.documentElement.dataset.motion = on ? "on" : "off";
        try {
            localStorage.setItem("abm-motion", on ? "on" : "off");
        } catch {
            /* storage optional */
        }
    }, [on]);

    useEffect(() => {
        if (!on) return;
        const animations = new Set<Animation>();
        const activePhotos = new Set<HTMLElement>();
        let frame = 0;
        let activeTilt: HTMLElement | null = null;
        const run = (
            element: Element,
            frames: Keyframe[],
            options: KeyframeAnimationOptions,
        ) => {
            const animation = element.animate(frames, options);
            animations.add(animation);
            animation.finished
                .then(() => animations.delete(animation))
                .catch(() => {});
        };
        const reveal = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    run(
                        entry.target,
                        [
                            { opacity: 0.65, transform: "translateY(28px)" },
                            { opacity: 1, transform: "translateY(0)" },
                        ],
                        { duration: 900, easing: "cubic-bezier(.16,1,.3,1)" },
                    );
                    entry.target
                        .querySelectorAll("[data-stagger]")
                        .forEach((child, i) =>
                            run(
                                child,
                                [
                                    { opacity: 0.65, transform: "translateY(18px)" },
                                    { opacity: 1, transform: "translateY(0)" },
                                ],
                                {
                                    duration: 700,
                                    delay: i * 55,
                                    easing: "cubic-bezier(.16,1,.3,1)",
                                },
                            ),
                        );
                    reveal.unobserve(entry.target);
                });
            },
            { threshold: 0.08 },
        );
        document
            .querySelectorAll(
                "[data-reveal], .section-heading, .page-hero-inner, .product-line, .event-card, .article-heading h1, .related-grid, .stats-band, .chapter-scene, .network-metrics, .contact-form, .support-experience, .compliance-panel",
            )
            .forEach((el) => reveal.observe(el));
        const update = () => {
            frame = 0;
            const d = document.documentElement;
            const progress =
                document.querySelector<HTMLElement>(".reading-progress");
            if (progress)
                progress.style.transform = `scaleX(${window.scrollY / Math.max(1, d.scrollHeight - innerHeight)})`;
            activePhotos.forEach((el) => {
                const r = el.getBoundingClientRect();
                const p = Math.max(
                    -1,
                    Math.min(
                        1,
                        (innerHeight / 2 - r.top - r.height / 2) / innerHeight,
                    ),
                );
                el.style.setProperty("--parallax", `${(p * 45).toFixed(1)}px`);
            });
        };
        const queue = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };
        const photoObserver = new IntersectionObserver((entries) =>
            entries.forEach((entry) => {
                const el = entry.target as HTMLElement;
                if (entry.isIntersecting) activePhotos.add(el);
                else activePhotos.delete(el);
                el.dataset.inView = String(entry.isIntersecting);
                queue();
            }),
        );
        document
            .querySelectorAll<HTMLElement>("[data-parallax], [data-ambient]")
            .forEach((el) => photoObserver.observe(el));
        const resetTilt = () => {
            if (!activeTilt) return;
            activeTilt.style.setProperty("--tilt-x", "0deg");
            activeTilt.style.setProperty("--tilt-y", "0deg");
            activeTilt = null;
        };
        const pointer = (event: PointerEvent) => {
            if (event.pointerType === "touch") return;
            const target = (event.target as HTMLElement).closest<HTMLElement>(
                "[data-tilt], .carrier-card, .path-card, .event-card, .media-row, .lead-card, .perks>div",
            );
            if (target !== activeTilt) resetTilt();
            if (!target) return;
            activeTilt = target;
            const rect = target.getBoundingClientRect();
            const x = (event.clientX - rect.left) / rect.width;
            const y = (event.clientY - rect.top) / rect.height;
            target.style.setProperty("--spot-x", `${x * 100}%`);
            target.style.setProperty("--spot-y", `${y * 100}%`);
            target.style.setProperty("--tilt-x", `${(y - 0.5) * -3}deg`);
            target.style.setProperty("--tilt-y", `${(x - 0.5) * 3}deg`);
        };
        window.addEventListener("scroll", queue, { passive: true });
        window.addEventListener("resize", queue, { passive: true });
        document.addEventListener("pointermove", pointer, { passive: true });
        document.addEventListener("pointerleave", resetTilt);
        update();
        return () => {
            cancelAnimationFrame(frame);
            reveal.disconnect();
            photoObserver.disconnect();
            animations.forEach((a) => a.cancel());
            activePhotos.forEach((el) =>
                el.style.setProperty("--parallax", "0px"),
            );
            resetTilt();
            window.removeEventListener("scroll", queue);
            window.removeEventListener("resize", queue);
            document.removeEventListener("pointermove", pointer);
            document.removeEventListener("pointerleave", resetTilt);
        };
    }, [path, on]);

    return (
        <>
            <div className="reading-progress" aria-hidden="true" />
            <button
                type="button"
                className="motion-toggle mono"
                aria-pressed={on}
                onClick={() => setOn((v) => !v)}
            >
                <span className="motion-toggle-glyph" aria-hidden="true">
                    {on ? "❙❙" : "▶"}
                </span>
                {on ? "PAUSE MOTION" : "MOTION ON"}
            </button>
        </>
    );
}
